import React, { useEffect, useCallback, useState } from 'react'
import { AccessQuestionMain } from './AccessQuestionMain'
import { resetSiteRoadWaysState, setSiteRoadwaysField } from '@modules/stepper/redux/siteRoadwaysSlice';
import { resetAmenityState, setAmenityField } from '@modules/stepper/redux/amenitesSlice';
import { AppDispatch, useAppSelector } from '@store/store';
import { useDispatch } from 'react-redux';
import { resetSiteRoadwaysROW, setSiteRoadwaysROWField } from '@modules/stepper-row/redux/siteRoadwaysROWSlice';
import { resetAmenityROWState, setAmenityROWField } from '@modules/stepper-row/redux/amenitesROWSlice';
import { getDashboardEditThunk } from '@modules/application-section/application-dashboard/redux/thunk/dashboardThunk';
import { resetApplicationSubmitted } from '@modules/application-section/application-details/redux/applicationSubmittedSlice';
import { setActiveStep } from '@modules/stepper/redux/stepperSlice';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { RowQuestionMain } from './RowQuestionMain';
import { Box } from '@mui/material';

const ClarificationQuestionsPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const accessId = searchParams.get("access");
    const rowId = searchParams.get("row");

    const applicationID = accessId || rowId;
    const [originalLocationTypeId, setOriginalLocationTypeId] = useState<string | number | null | undefined>(undefined);
    const clarificationData = useAppSelector((state) => state.clarification.clarificationData);

    const documents_to_updated = (clarificationData?.documents_to_updated?.length ?? 0) > 0;
    const questions_to_updated = clarificationData?.questions_to_updated;

    // console.log("clarificationData", clarificationData);

    const handleEdit = useCallback(async () => {
        if (!applicationID) return;
        setOriginalLocationTypeId(undefined);

        // 🔁 Reset all states
        dispatch(resetSiteRoadWaysState());
        dispatch(resetAmenityState());
        dispatch(resetSiteRoadwaysROW());
        dispatch(resetAmenityROWState());

        let selectedCatId = 0;
        if (accessId) selectedCatId = 1;
        else if (rowId) selectedCatId = 2;
        if (!selectedCatId) return;

        // 🔹 Fetch edit payload
        const result = await dispatch(
            getDashboardEditThunk({
                access_id: applicationID as any,
                application_categroy_id: selectedCatId,
            }),
        );

        if (result?.meta?.requestStatus !== "fulfilled") return;

        const applicationData: any = result?.payload?.data || {};
        setOriginalLocationTypeId(applicationData.location_type_id ?? null);

        // 🔹 Helper: boolean → Yes / No
        const booleanToYesNo = (value: boolean | null | undefined) => {
            if (value === true) return "Yes";
            if (value === false) return "No";
            return "";
        };

        // 🔹 Helper: prevent overwriting with empty values
        const safeDispatch = (action: any, name: string, value: any) => {
            if (value !== null && value !== undefined && value !== "") {
                dispatch(action({ name, value }));
            }
        };

        /* =====================================================
        ================= ACCESS APPLICATION ================
         ===================================================== */
        if (selectedCatId === 1) {
            safeDispatch(setAmenityField, "access_id", applicationData?.id);

            /**
             * Fields that must be stored as "Yes" / "No" strings (radio fields),
             * even though the API may return them as booleans (true / false).
             */
            const booleanToYesNoFields = [
                "is_service_road_provided",
                "is_constructed_without_prior_permission",
                "is_facility_operational",
                "is_facility_operational_consent",
                "is_part_of_rest_area",
                "on_slip_road",
                // "area_acquired",
                "access_criteria_met",
                "is_located_on_service_road",
                "meets_general_access_requirements",
                "is_coco_fuel_station",
                "fuel_station_carriageway_type",
            ];

            // 🔥 Keys that must NOT be blindly copied from API — they are recomputed below
            const SKIP_KEYS = new Set([
                "questions",
                "question_true",
                "question_false",
                "questionList",
                "selectedQuestions",
            ]);

            // 🔥 Single pass: normalize booleans → "Yes"/"No" once, dispatch to BOTH slices
            Object.entries(applicationData).forEach(([name, value]) => {
                if (SKIP_KEYS.has(name)) {
                    // console.log("[handleEdit] skipping meta key:", name);
                    return;
                }

                const normalizedValue = booleanToYesNoFields.includes(name)
                    ? booleanToYesNo(value as boolean)
                    : value;

                safeDispatch(setAmenityField, name, normalizedValue);
                safeDispatch(setSiteRoadwaysField, name, normalizedValue);
            });

            /**
             * 🔥 Fallback: if API doesn't send `is_coco_fuel_station` at all,
             * default it to "No" so LOI fields become visible.
             */
            const cocoValue = applicationData?.is_coco_fuel_station;
            if (cocoValue === undefined || cocoValue === null || cocoValue === "") {
                safeDispatch(setSiteRoadwaysField, "is_coco_fuel_station", "No");
                safeDispatch(setAmenityField, "is_coco_fuel_station", "No");
            }

            /* =========================================================
               🔥 CRITICAL FIX
               Dispatch numeric IDs to BOTH slices.
               `AccessQuestionMain` reads `property_type_id` from the
               `amenities` slice to decide which section variant to
               render (Residential vs Other, Urban vs Rural). If we
               only set it on `siteRoadways`, `getDynamicSections`
               will see property_type_id="" and fall back to only the
               base section — hiding "Access Road & Turning Requirements"
               (ids 322, 323, 324) and everything after.
               ========================================================= */
            safeDispatch(setSiteRoadwaysField, "property_type_id", applicationData?.property_type_id);
            safeDispatch(setSiteRoadwaysField, "location_type_id", applicationData?.location_type_id);
            safeDispatch(setSiteRoadwaysField, "service_road_type_id", applicationData?.service_road_type_id);

            safeDispatch(setAmenityField, "property_type_id", applicationData?.property_type_id);
            safeDispatch(setAmenityField, "location_type_id", applicationData?.location_type_id);
            safeDispatch(setAmenityField, "service_road_type_id", applicationData?.service_road_type_id);
            safeDispatch(setAmenityField, "category_id", applicationData?.category_id);

            // "Others" property type text — only relevant when property_type_id === 6
            if (Number(applicationData?.property_type_id) === 6) {
                safeDispatch(
                    setSiteRoadwaysField,
                    "othersPropertyType",
                    applicationData?.othersPropertyType,
                );
            }

            /* ---------- QUESTIONS → RADIO MAPPING (must run LAST) ---------- */
            const questionTrue: number[] = [];
            const questionFalse: number[] = [];

            applicationData?.questions?.forEach((q: any) => {
                if (!q?.question_id) return;

                const val = String(q.value);   // normalize to string
                if (val === "Yes") questionTrue.push(q.question_id);
                if (val === "No") questionFalse.push(q.question_id);

                // Store each question's answer under its numeric id
                safeDispatch(setSiteRoadwaysField, String(q.question_id), val);
            });

            // 🔥 Raw dispatch — bypass safeDispatch's empty-string guard so an
            // empty array still clears stale values.
            dispatch(setSiteRoadwaysField({ name: "question_true", value: questionTrue }));
            dispatch(setSiteRoadwaysField({ name: "question_false", value: questionFalse }));

            // console.log("[handleEdit] recomputed questionTrue:", questionTrue);
            // console.log("[handleEdit] recomputed questionFalse:", questionFalse);

            safeDispatch(setSiteRoadwaysField, "is_previously_approved", false);

            dispatch(setActiveStep(applicationData?.form_step || 0));
            dispatch(resetApplicationSubmitted());
            // navigate("/stepper");
        }

        /* =====================================================
         ================= ROW APPLICATION ===================
         ===================================================== */
        if (selectedCatId === 2) {
            safeDispatch(setAmenityROWField, "application_id", applicationData?.id);
            safeDispatch(setSiteRoadwaysROWField, "application_id", applicationData?.id);
            safeDispatch(setAmenityROWField, "access_id", applicationData?.id);

            if (Number(applicationData?.property_type_id) === 13) {
                safeDispatch(
                    setSiteRoadwaysROWField,
                    "othersPropertyType",
                    applicationData?.othersPropertyType,
                );
            }

            if (Number(applicationData?.service_road_type_id) === 1) {
                safeDispatch(
                    setSiteRoadwaysROWField,
                    "is_located_on_service_road",
                    booleanToYesNo(applicationData?.is_located_on_service_road),
                );
                safeDispatch(
                    setSiteRoadwaysROWField,
                    "meets_general_access_requirements",
                    booleanToYesNo(applicationData?.meets_general_access_requirements),
                );
            }

            const yesIds: number[] = [];
            const noIds: number[] = [];

            applicationData?.questions?.forEach((q: any) => {
                const val = String(q.value).toLowerCase();

                if (val === "yes") {
                    yesIds.push(q.question_id);
                } else if (val === "no") {
                    noIds.push(q.question_id);
                }
            });

            Object.entries(applicationData).forEach(([name, value]) => {
                safeDispatch(setAmenityROWField, name, value);
            });

            Object.entries(applicationData).forEach(([name, value]) => {
                if (value !== null && value !== undefined && value !== "") {
                    safeDispatch(setSiteRoadwaysROWField, name, String(value));
                }
            });

            safeDispatch(setSiteRoadwaysROWField, "questions_true", yesIds);
            safeDispatch(setSiteRoadwaysROWField, "questions_false", noIds);

            dispatch(setActiveStep(applicationData?.form_step || 0));
            dispatch(resetApplicationSubmitted());
            // navigate("/stepper-row");
        }
    }, [applicationID, accessId, rowId, dispatch, navigate]);

    // 🔥 Trigger handleEdit whenever applicationID changes
    useEffect(() => {
        handleEdit();
    }, [handleEdit]);

    return (
        <Box sx={{ padding: { xs: "0px 30px", md: "0px 60px" } }}>
            {accessId ? (originalLocationTypeId !== undefined && <AccessQuestionMain originalLocationTypeId={originalLocationTypeId} collapsed={false} restrictedFields={[]
                // ["is_constructed_without_prior_permission", "is_facility_operational"]
                } />) : <RowQuestionMain />}
        </Box>
    )
}

export default ClarificationQuestionsPage